import React, { useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { X, Wifi, Shield, Check, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { BrowserProvider } from 'ethers';

/**
 * 화면은 이전과 같다. 바뀐 건 "어떻게 연결하는가"뿐이다. (2026-09-14)
 *
 * 고친 것 1 — 지갑을 제대로 골라 잡는다
 *   예전에는 어느 버튼을 눌러도 window.ethereum 하나만 잡았다. 지갑이 여러 개
 *   깔려 있으면 메타마스크를 눌렀는데 다른 지갑이 열린다. 이제 EIP-6963
 *   (지갑들이 자기를 알리는 표준)으로 각 지갑을 구분해 잡고, 그걸 지원하지 않는
 *   구버전은 isMetaMask / isTrust / isCoinbaseWallet 플래그로 고른다.
 *
 * 고친 것 2 — 핸디체인으로 전환한다
 *   연결 후 chainId 88003 으로 전환하고, 지갑에 그 체인이 없으면 추가를 띄운다.
 *   RPC는 서버가 알려준다(/api/wallet-link/chain).
 *
 * 고친 것 3 — 연결한 주소를 서버가 알게 한다
 *   예전에는 localStorage 에만 넣어서 서버는 그 주소를 몰랐다(지급 대상으로 못 씀).
 *   이제 논스에 서명해 소유를 증명하고 linked_wallets 에 등록한다.
 */

interface WalletConnectModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConnect: (walletType: string) => void;
}

const wallets = [
  {
    id: 'foundation',
    name: 'Handy Foundation',
    icon: '🏛️',
    description: 'Official foundation wallet',
    color: 'from-green-500 to-emerald-600',
    isFoundation: true
  },
  {
    id: 'metamask',
    name: 'MetaMask',
    icon: '🦊',
    description: 'Popular Web3 wallet',
    color: 'from-orange-500 to-yellow-500'
  },
  {
    id: 'trustwallet',
    name: 'Trust Wallet',
    icon: '🛡️',
    description: 'Secure mobile wallet',
    color: 'from-blue-500 to-blue-600'
  },
  {
    id: 'coinbase',
    name: 'Coinbase Wallet',
    icon: '💙',
    description: 'Self-custody wallet',
    color: 'from-blue-600 to-indigo-600'
  }
];

// 버튼 id -> (EIP-6963 rdns 후보, 구버전 판별 플래그)
const MATCH: Record<string, { rdns: string[]; flag: string }> = {
  metamask:    { rdns: ['io.metamask'], flag: 'isMetaMask' },
  trustwallet: { rdns: ['com.trustwallet.app', 'com.trustwallet'], flag: 'isTrust' },
  coinbase:    { rdns: ['com.coinbase.wallet'], flag: 'isCoinbaseWallet' },
};

const API = (import.meta as any).env?.VITE_API_URL || '';

export function WalletConnectModal({ isOpen, onClose, onConnect }: WalletConnectModalProps) {
  const [connecting, setConnecting] = useState(null as string | null);
  const found = useRef<any[]>([]);          // EIP-6963 으로 발견한 지갑들
  const chain = useRef<any>(null);          // 서버가 알려준 체인 정보

  // EIP-6963: 지갑들이 announce 이벤트로 자기를 알린다. 모달이 열릴 때 요청한다.
  useEffect(() => {
    if (!isOpen) return;
    found.current = [];
    const onAnnounce = (e: any) => {
      const d = e.detail;
      if (!d?.info?.uuid) return;
      if (found.current.some((f: any) => f.info.uuid === d.info.uuid)) return;
      found.current.push(d);
    };
    window.addEventListener('eip6963:announceProvider', onAnnounce as any);
    window.dispatchEvent(new Event('eip6963:requestProvider'));

    fetch(`${API}/api/wallet-link/chain`)
      .then(r => r.json())
      .then(d => { if (d.success) chain.current = d; })
      .catch(() => { /* 체인 정보 실패해도 연결 자체는 가능 */ });

    return () => window.removeEventListener('eip6963:announceProvider', onAnnounce as any);
  }, [isOpen]);

  /** 버튼에 맞는 provider 를 고른다. 못 찾으면 null. */
  const resolveProvider = (walletId: string): any | null => {
    const m = MATCH[walletId];
    if (!m) return null;

    // 1) EIP-6963 (표준, 지갑이 여러 개여도 정확히 구분됨)
    const byRdns = found.current.find((f: any) =>
      m.rdns.some(r => String(f.info?.rdns || '').toLowerCase() === r));
    if (byRdns) return byRdns.provider;

    // 2) 구버전: window.ethereum.providers 배열에서 플래그로 고른다
    const eth = (window as any).ethereum;
    if (!eth) return null;
    if (Array.isArray(eth.providers)) {
      const p = eth.providers.find((x: any) => x?.[m.flag]);
      if (p) return p;
    }
    // 3) 단일 주입 지갑: 플래그가 맞을 때만 쓴다(엉뚱한 지갑이 열리는 걸 막는다)
    return eth[m.flag] ? eth : null;
  };

  /** 핸디체인으로 전환. 없으면 추가 요청. 실패해도 연결 자체는 유지한다. */
  const ensureChain = async (provider: any) => {
    const c = chain.current;
    if (!c?.ready) return;
    try {
      await provider.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId: c.chainIdHex }],
      });
    } catch (e: any) {
      const code = e?.code ?? e?.data?.originalError?.code;
      if (code === 4902) {
        await provider.request({
          method: 'wallet_addEthereumChain',
          params: [{
            chainId: c.chainIdHex,
            chainName: c.chainName,
            nativeCurrency: c.nativeCurrency,
            rpcUrls: c.rpcUrls,
            blockExplorerUrls: c.blockExplorerUrls?.length ? c.blockExplorerUrls : undefined,
          }],
        });
      }
      // 그 밖의 실패(사용자 취소 포함)는 연결을 막지 않는다
    }
  };

  /** 서명으로 소유를 증명하고 서버에 등록한다. */
  const registerOnServer = async (provider: any, address: string, walletType: string) => {
    const t = localStorage.getItem('handy-jwt-token') || '';
    const headers: any = { 'Content-Type': 'application/json' };
    if (t) headers.Authorization = `Bearer ${t}`;

    const nr = await fetch(`${API}/api/wallet-link/nonce?address=${address}`, { headers });
    const nd = await nr.json();
    if (!nr.ok || !nd.success) throw new Error(nd.error || '인증코드 발급 실패');

    const signature = await provider.request({
      method: 'personal_sign',
      params: [nd.message, address],
    });

    const vr = await fetch(`${API}/api/wallet-link/verify`, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        address, signature, nonce: nd.nonce, walletType,
        chainId: chain.current?.chainId,
      }),
    });
    const vd = await vr.json();
    if (!vr.ok || !vd.success) throw new Error(vd.error || '지갑 등록 실패');
    return vd.address as string;
  };

  const handleConnect = async (walletId: string, walletName: string) => {
    setConnecting(walletId);
    try {
      if (walletId === 'foundation') {
        // [2026-09-12] 바톤 잔재 제거.
        // 여기에 '0xVATTONFOUNDATION000000000000000A888888' 이라는 가짜 주소가
        // 박혀 있었다. 유효한 지갑 주소가 아니라 잔액 조회가 늘 실패했고,
        // 그 주소를 localStorage 에 저장까지 해서 이후 동작이 꼬일 수 있었다.
        // 재단 지갑은 서버 환경변수(TREASURY_ADDRESS)로만 관리한다.
        // 프론트에서 재단 지갑에 직접 연결할 일은 없으므로 기능을 막는다.
        toast.info('재단 지갑 연결은 지원하지 않습니다.');
        setConnecting(null);
        return;
      }

      const injected = resolveProvider(walletId);
      if (!injected) throw new Error('NO_PROVIDER');

      const provider = new BrowserProvider(injected);
      const accounts = await provider.send('eth_requestAccounts', []);
      if (!accounts || accounts.length === 0) throw new Error('계정을 가져오지 못했습니다');
      const address = accounts[0] as string;

      await ensureChain(injected);

      // 서명을 거부해도 연결 자체는 살린다(예전 동작과 같게).
      try {
        await registerOnServer(injected, address, walletName);
      } catch { /* 등록 실패해도 연결은 유지 */ }

      localStorage.setItem('handy-wallet-address', address);
      localStorage.setItem('handy-wallet-type', walletId);
      onConnect(walletName + ':' + address);
      toast.success(`🎉 ${walletName} 연결 완료: ${address.slice(0, 6)}...${address.slice(-4)}`);
      onClose();
    } catch (error: any) {
      if (error?.message === 'NO_PROVIDER') {
        toast.error(`${walletName} 확장앱/브라우저를 찾을 수 없습니다. ${walletName} 앱 내 브라우저로 열어주세요.`);
      } else if (error?.code === 4001) {
        toast.error('연결 요청을 취소했습니다.');
      } else {
        toast.error(`${walletName} 연결 실패`);
      }
    } finally {
      setConnecting(null);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md mx-auto bg-background border-border w-[95vw] max-h-[85vh] overflow-auto">
        <DialogHeader>
          <DialogTitle className="text-center text-xl font-bold text-foreground">
            Connect to Web3 Wallet
          </DialogTitle>
          <DialogDescription className="text-center text-muted-foreground">
            Choose your preferred wallet to connect
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 mt-6">
          {wallets.map((wallet) => (
            <button
              key={wallet.id}
              onClick={() => handleConnect(wallet.id, wallet.name)}
              disabled={connecting !== null}
              className={`w-full p-4 border rounded-2xl transition-all duration-200 group ${connecting && connecting !== wallet.id ? 'opacity-50 cursor-not-allowed' : ''} ${
                wallet.isFoundation
                  ? 'bg-gradient-to-r from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-green-200 dark:border-green-700'
                  : 'bg-card border-border hover:border-primary'
              }`}
            >
              <div className="flex items-center space-x-4">
                <div className={`w-12 h-12 bg-gradient-to-r ${wallet.color} rounded-2xl flex items-center justify-center text-2xl shadow-lg group-hover:scale-105 transition-transform ${
                  wallet.isFoundation ? 'ring-2 ring-green-300 dark:ring-green-600' : ''
                }`}>
                  {wallet.icon}
                </div>
                <div className="flex-1 text-left">
                  <h3 className={`font-bold ${wallet.isFoundation ? 'text-green-700 dark:text-green-300' : 'text-foreground'}`}>
                    {wallet.name}
                    {wallet.isFoundation && <span className="ml-2 text-xs bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 px-2 py-1 rounded-full">공식</span>}
                  </h3>
                  <p className={`text-sm ${wallet.isFoundation ? 'text-green-600 dark:text-green-400' : 'text-muted-foreground'}`}>
                    {wallet.description}
                  </p>

                </div>
                {connecting === wallet.id ? (
                  <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
                ) : (
                  <div className="w-6 h-6 text-muted-foreground group-hover:text-primary transition-colors">
                    →
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>

        {/* App Permissions */}
        <div className="mt-6 p-4 bg-muted rounded-2xl">
          <h4 className="font-bold text-foreground mb-3 flex items-center">
            <Shield className="w-4 h-4 mr-2 text-blue-600" />
            App Permissions
          </h4>
          <div className="space-y-2">
            <div className="flex items-center space-x-3">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-sm text-muted-foreground">View your wallet balance and activity</span>
            </div>
            <div className="flex items-center space-x-3">
              <Check className="w-4 h-4 text-green-600" />
              <span className="text-sm text-muted-foreground">Request approval for transactions</span>
            </div>
          </div>
        </div>

        {/* Networks */}
        <div className="mt-4 p-4 bg-muted rounded-2xl">
          <h4 className="font-bold text-foreground mb-3 flex items-center">
            <Wifi className="w-4 h-4 mr-2 text-purple-600" />
            Supported Networks
          </h4>
          <div className="flex items-center space-x-2">
            <div className="w-6 h-6 bg-gradient-to-r from-blue-600 to-blue-700 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">E</span>
            </div>
            <div className="w-6 h-6 bg-gradient-to-r from-purple-600 to-pink-600 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">P</span>
            </div>
            <div className="w-6 h-6 bg-gradient-to-r from-orange-500 to-red-500 rounded-full flex items-center justify-center">
              <span className="text-white text-xs font-bold">B</span>
            </div>
            <div className="w-6 h-6 bg-gradient-to-r from-green-500 to-green-600 rounded-full">
              <span className="text-white text-xs font-bold flex items-center justify-center w-full h-full">A</span>
            </div>
          </div>
        </div>

        <Button
          variant="outline"
          onClick={onClose}
          className="w-full mt-4 border-border text-muted-foreground hover:bg-accent"
        >
          Cancel Connection
        </Button>
      </DialogContent>
    </Dialog>
  );
}
